/**
 * Utility functions for creating and handling shareable Package Finder links
 */

import { ShareableContent } from './sharingUrls';
import { fitShareImageUrl, getConfiguredShareImage, getPublicSiteUrl, normalizePublicImageUrl } from './publicSiteUrl';

export interface PackageFinderParams {
  open?: boolean; // Open the wizard immediately
  category?: string; // Pre-select a category
  duration?: string; // Pre-select a duration
  location?: string; // Pre-select a location
  people?: string; // Pre-select number of people
}

/**
 * Generate shareable content for Package Finder
 * @param params - Optional parameters to pre-fill the wizard
 * @returns ShareableContent object for use with ShareButton/ShareModal
 */
export function generatePackageFinderShareContent(
  params: PackageFinderParams = {}
): ShareableContent {
  const baseUrl = getPublicSiteUrl();
  const url = generatePackageFinderUrl(params, baseUrl);
  
  // Try to get share image from localStorage first
  let imageUrl = '';
  try {
    const shareImageData = localStorage.getItem('package_share_image');
    if (shareImageData) {
      const shareImageParsed = JSON.parse(shareImageData);
      const publicShareImage = normalizePublicImageUrl(shareImageParsed.imageUrl);
      if (publicShareImage) {
        imageUrl = fitShareImageUrl(publicShareImage);
      }
    }
  } catch (e) {
    // Fallback to logo
  }
  
  // Fallback to logo if no share image
  if (!imageUrl) {
    try {
      const logoData = localStorage.getItem('website_logo');
      if (logoData) {
        const logoParsed = JSON.parse(logoData);
        const publicLogo = normalizePublicImageUrl(logoParsed.imageUrl);
        if (publicLogo) {
          imageUrl = fitShareImageUrl(publicLogo);
        }
      }
    } catch (e) {
      // Final fallback
    }
  }
  
  // Final fallback to default logo
  if (!imageUrl) {
    imageUrl = getConfiguredShareImage();
  }
  
  const title = 'Find Your Perfect Photography Package - Carmen Lorraine Photography';
  
  // Create engaging description based on whether category is specified
  let description = '';
  if (params.category) {
    description = `📸 Discover Amazing ${params.category} Photography Packages!\n\n`;
    description += `Check out this Package Finder tool to discover the perfect ${params.category.toLowerCase()} photography package tailored to your needs.\n\n`;
    description += `✨ Features:\n`;
    description += `• Personalized package recommendations\n`;
    description += `• Filter by category, duration, and more\n`;
    description += `• See pricing and package details\n`;
    description += `• Find exactly what you're looking for\n\n`;
    description += `🎯 Perfect for anyone looking for professional ${params.category.toLowerCase()} photography services!\n\n`;
    description += `Click the link below to explore available packages and find your perfect match! 👇`;
  } else {
    description = `📸 Find Your Perfect Photography Package!\n\n`;
    description += `Explore this Package Finder tool to discover the perfect photography package for your needs.\n\n`;
    description += `✨ What you can do:\n`;
    description += `• Browse all available photography packages\n`;
    description += `• Filter by category, duration, location, and more\n`;
    description += `• Get personalized recommendations\n`;
    description += `• See detailed pricing and package information\n\n`;
    description += `🎯 Whether you're planning a wedding, need portrait photography, or looking for event coverage, this tool makes it easy to find exactly what you need!\n\n`;
    description += `Click the link below to start exploring! 👇`;
  }
  
  return {
    title,
    description,
    image: imageUrl,
    url,
    hashtags: ['photography', 'packagefinder', 'carmenlorraine', params.category?.toLowerCase() || 'photography'].filter(Boolean),
    category: params.category || 'Photography'
  };
}

/**
 * Generate a shareable URL for the Package Finder
 * @param params - Optional parameters to pre-fill the wizard
 * @param baseUrl - Base URL (defaults to current origin)
 * @returns Shareable URL string
 */
export function generatePackageFinderUrl(
  params: PackageFinderParams = {},
  baseUrl?: string
): string {
  const url = new URL(baseUrl || getPublicSiteUrl());
  url.pathname = '/';
  
  // Always set open=true to trigger the wizard
  const searchParams = new URLSearchParams();
  searchParams.set('package-finder', 'true');
  
  // Add optional parameters
  if (params.category) {
    searchParams.set('category', params.category);
  }
  if (params.duration) {
    searchParams.set('duration', params.duration);
  }
  if (params.location) {
    searchParams.set('location', params.location);
  }
  if (params.people) {
    searchParams.set('people', params.people);
  }
  
  url.search = searchParams.toString();
  return url.toString();
}

/**
 * Parse Package Finder parameters from URL
 * @param searchParams - URLSearchParams or window.location.search
 * @returns Parsed parameters
 */
export function parsePackageFinderParams(
  searchParams?: URLSearchParams | string
): PackageFinderParams {
  let params: URLSearchParams;
  
  if (typeof searchParams === 'string') {
    params = new URLSearchParams(searchParams);
  } else if (searchParams) {
    params = searchParams;
  } else {
    params = new URLSearchParams(window.location.search);
  }
  
  const result: PackageFinderParams = {};
  
  if (params.get('package-finder') === 'true') {
    result.open = true;
  }
  
  const category = params.get('category');
  if (category) {
    result.category = category;
  }
  
  const duration = params.get('duration');
  if (duration) {
    result.duration = duration;
  }
  
  const location = params.get('location');
  if (location) {
    result.location = location;
  }
  
  const people = params.get('people');
  if (people) {
    result.people = people;
  }
  
  return result;
}

/**
 * Copy Package Finder URL to clipboard
 * @param params - Optional parameters to pre-fill the wizard
 * @returns Promise that resolves when URL is copied, rejects on failure
 */
export async function copyPackageFinderUrl(
  params: PackageFinderParams = {}
): Promise<void> {
  const url = generatePackageFinderUrl(params);
  
  // Try modern clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      console.log('✅ Package Finder URL copied to clipboard:', url);
      return; // Success, exit early
    } catch (error) {
      console.warn('⚠️ Clipboard API failed, trying fallback:', error);
      // Continue to fallback method
    }
  }
  
  // Fallback: create a temporary textarea
  try {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);
    
    // Select and copy
    textarea.select();
    textarea.setSelectionRange(0, url.length); // For mobile devices
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    if (successful) {
      console.log('✅ Package Finder URL copied (fallback method):', url);
      return; // Success
    } else {
      throw new Error('execCommand copy failed');
    }
  } catch (fallbackError) {
    console.error('❌ All copy methods failed:', fallbackError);
    throw new Error('Failed to copy URL to clipboard. Please copy manually: ' + url);
  }
}

/**
 * Generate a WhatsApp share link with Package Finder URL
 * @param params - Optional parameters to pre-fill the wizard
 * @param message - Optional custom message
 * @returns WhatsApp URL string
 */
export function generateWhatsAppShareUrl(
  params: PackageFinderParams = {},
  message?: string
): string {
  const url = generatePackageFinderUrl(params);
  const defaultMessage = "I'd like to find the perfect photography package for me. Can you help?";
  const shareMessage = message || defaultMessage;
  const fullMessage = `${shareMessage}\n\n${url}`;
  
  return `https://wa.me/?text=${encodeURIComponent(fullMessage)}`;
}

/**
 * Generate an email share link with Package Finder URL
 * @param params - Optional parameters to pre-fill the wizard
 * @param subject - Optional email subject
 * @param body - Optional email body
 * @returns mailto URL string
 */
export function generateEmailShareUrl(
  params: PackageFinderParams = {},
  subject?: string,
  body?: string
): string {
  const url = generatePackageFinderUrl(params);
  const emailSubject = subject || 'Find Your Perfect Photography Package';
  const emailBody = body || `Hi,\n\nI'd like to find the perfect photography package. Please use this link:\n\n${url}\n\nThank you!`;
  
  return `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
}

